import { getSizedCalmAssetUrl } from '@/utils/images/imageLoader';
import { Box, SxProps, Theme } from '@rhg/libs/material';
import {
    MaskOrientation,
    styleMaskedContainer,
    sxAppend,
} from '@rhg/libs/material/sx';
import { StaticImageData } from '@rhg/libs/next/image';
import { resolveImageSrc } from '@rhg/utils/images/resolveImageSrc';
import { PropsWithChildren } from 'react';

/**
 * Width requested from the assets.calm.com CDN for the background image. The
 * image is blurred (default 50px) and sits behind an opacity mask, so detail
 * is invisible — a small render is indistinguishable from full-res but a
 * fraction of the bytes. Only applies to assets.calm.com URLs.
 */
const DEFAULT_BACKGROUND_IMAGE_WIDTH = 480;

const styleBlurredBackgroundContainer = {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
};

const styleBlurredImageContainer = {
    // `position: relative` makes this the containing block for the absolutely
    // positioned blurred image below, so its `overflow: hidden` actually clips
    // that image. The image is sized `calc(100% + blurPx * 2)` to hide the
    // blur's soft edges; without this the image resolves to a positioned
    // ancestor and its extra width leaks past the viewport, adding horizontal
    // page scroll (visible on any header with a hero image).
    position: 'relative',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
};

function styleBlurredImage(blurPx: number, hideOverlay: boolean) {
    const aHundoPlusBlur = `calc(100% + ${blurPx * 2}px)`;
    return {
        position: 'absolute',
        width: aHundoPlusBlur,
        height: aHundoPlusBlur,
        backgroundSize: 'cover',
        backgroundPositionX: 'center',
        backgroundPositionY: 'center',
        filter: `blur(${blurPx}px)`,

        /**
         * Solves: EVE-11279 and EVE-11270
         * need to create a new stacking context to contain the blur effect in Safari
         * https://stackoverflow.com/a/64063023/3765769
         */
        willChange: 'transform',

        ...(!hideOverlay && {
            '::before': {
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: (theme: Theme) =>
                    theme.palette.mode === 'light'
                        ? theme.palette.mask.white['80p']
                        : theme.palette.mask.black['60p'],
                content: '""',
            },
        }),
    };
}

const styleBlurredBackgroundImageAndContentContainer = { position: 'relative' };

interface BlurredBackgroundImageProps {
    /** Image URL or StaticImageData for the background */
    src?: string | StaticImageData;
    /** Mask gradient orientation (Top, Right, Bottom, Left) */
    orientation: MaskOrientation;
    /** MUI sx prop for custom styling */
    sx?: SxProps<Theme>;
    /** Blur amount in pixels (default: 50) */
    blurPx?: number;
    /**
     * Controls where the gradient mask transitions from transparent to opaque.
     * Range: 0-1, where 0.5 means the gradient reaches full opacity at 50% of the distance. (default: 1)
     * Lower values create a sharper transition, higher values create a softer, more gradual fade.
     */
    maskedProportion?: number;
    /**
     * Controls where the gradient mask starts transitioning from transparent to opaque.
     * Range: 0-1, where 0 (default) means the gradient starts at the very beginning.
     * Higher values move the start point further along the element.
     */
    maskedStartProportion?: number;
    /**
     * Width (px) to request from the assets.calm.com CDN for the background
     * image. Defaults to {@link DEFAULT_BACKGROUND_IMAGE_WIDTH}. Because the
     * image is blurred and masked, a small value is visually lossless.
     */
    backgroundImageWidth?: number;
    /**
     * Omit the light/dark tint overlay normally drawn over the blurred image.
     * The overlay exists to keep text legible over the hero (default: false,
     * matching every current use) — skip it for heroes with no text on top.
     */
    hideOverlay?: boolean;
}

/**
 * Renders a blurred background image with gradient mask overlay. Children appear on top of the background.
 *
 * **Important**: Child elements must have a `position` value other than `static` (e.g., `'relative'`,
 * `'absolute'`, or `'fixed'`) and a `z-index` to reliably appear above the background and mask.
 * Elements that are statically positioned will render beneath the blurred background image and only appear where
 * the background is transparent.
 *
 * @example
 * <BlurredBackgroundImage src={url} orientation={MaskOrientation.Top}>
 *   <Box sx={{ position: 'relative', zIndex: 1 }}>Content on top</Box>
 * </BlurredBackgroundImage>
 */
export function BlurredBackgroundImage({
    src,
    sx,
    orientation,
    blurPx = 50,
    maskedStartProportion = 0,
    maskedProportion = 1,
    backgroundImageWidth = DEFAULT_BACKGROUND_IMAGE_WIDTH,
    hideOverlay = false,
    children,
}: PropsWithChildren<BlurredBackgroundImageProps>) {
    const backgroundUrl = src
        ? getSizedCalmAssetUrl(resolveImageSrc(src), backgroundImageWidth)
        : undefined;
    return (
        <Box
            data-testid="blurred-background-image"
            sx={sxAppend(styleBlurredBackgroundImageAndContentContainer, sx)}
        >
            <Box sx={styleBlurredBackgroundContainer}>
                <Box
                    data-testid="blur-image-container"
                    sx={sxAppend(
                        styleBlurredImageContainer,
                        styleMaskedContainer(
                            orientation,
                            maskedProportion,
                            maskedStartProportion
                        )
                    )}
                >
                    <Box
                        sx={sxAppend(
                            styleBlurredImage(blurPx, hideOverlay),
                            backgroundUrl && {
                                backgroundImage: `url(${backgroundUrl})`,
                            }
                        )}
                    />
                </Box>
            </Box>
            {children}
        </Box>
    );
}
